import {
  RectangularDuct, RoundDuct, OvalDuct, TransitionDuct,
  RadialElbow, RectangularElbow, RoundElbow, DaltTestResults,
} from '../../models/types';

export const calcRectangularDuctArea = (d: RectangularDuct) => 2 * (d.width + d.height) * d.length;
export const calcRoundDuctArea = (d: RoundDuct) => Math.PI * d.diameter * d.length;
export const calcOvalDuctArea = (d: OvalDuct) => {
  const a = d.majorAxis / 2, b = d.minorAxis / 2;
  return Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b))) * d.length;
};
export const calcTransitionDuctArea = (d: TransitionDuct) => {
  const inlet = 2 * (d.inletWidth + d.inletHeight);
  const outlet = 2 * (d.outletWidth + d.outletHeight);
  return ((inlet + outlet) / 2) * d.length;
};
export const calcRadialElbowArea = (e: RadialElbow) => {
  const innerArc = (Math.PI * e.insideRadius * e.elbowAngle) / 180;
  const outerArc = (Math.PI * e.outsideRadius * e.elbowAngle) / 180;
  const avgW = (e.inletWidth + e.outletWidth) / 2;
  const avgH = (e.inletHeight + e.outletHeight) / 2;
  // fix B-1: both curved side panels use (innerArc + outerArc), not their difference
  const curved = (innerArc + outerArc) * (avgW + avgH);
  const faces = e.inletWidth * e.inletHeight + e.outletWidth * e.outletHeight;
  return (curved + faces) * e.quantity;
};
export const calcRectangularElbowArea = (e: RectangularElbow) => {
  const top = e.overallWidth * e.overallLength;
  const bottom = e.overallWidth * e.overallLength;
  // fix B-2: side panels use overallWidth (perpendicular to throat), not overallLength
  const side1 = e.overallWidth * e.inletHeight;
  const side2 = e.overallWidth * e.outletHeight;
  const inlet = e.inletWidth * e.inletHeight;
  const outlet = e.outletWidth * e.outletHeight;
  return (top + bottom + side1 + side2 + inlet + outlet) * e.quantity;
};
export const calcRoundElbowArea = (e: RoundElbow) =>
  Math.PI * e.diameter * ((Math.PI * e.radius * e.angle) / 180) * e.quantity;

export const calcTotalSurfaceArea = (
  rect: RectangularDuct[], round: RoundDuct[], oval: OvalDuct[], trans: TransitionDuct[],
  radial: RadialElbow[], rectElbow: RectangularElbow[], roundElbow: RoundElbow[]
): number =>
  rect.reduce((s, d) => s + calcRectangularDuctArea(d), 0) +
  round.reduce((s, d) => s + calcRoundDuctArea(d), 0) +
  oval.reduce((s, d) => s + calcOvalDuctArea(d), 0) +
  trans.reduce((s, d) => s + calcTransitionDuctArea(d), 0) +
  radial.reduce((s, e) => s + calcRadialElbowArea(e), 0) +
  rectElbow.reduce((s, e) => s + calcRectangularElbowArea(e), 0) +
  roundElbow.reduce((s, e) => s + calcRoundElbowArea(e), 0);

export const calcLeakageFactor = (lc: number, tp: number) =>
  lc > 0 && tp > 0 ? lc * Math.pow(tp, 0.65) : 0;

export const calcAllowableLeakage = (areaSqIn: number, leakageFactor: number) =>
  (areaSqIn / 144 / 100) * leakageFactor;

export const calcActualLeakage = (pressureDrop: number, calibConst: number) =>
  pressureDrop >= 0 && calibConst > 0 ? calibConst * Math.sqrt(pressureDrop) : 0;

export const calcTestLeakageClass = (actual: number, areaSqIn: number, tp: number) =>
  areaSqIn > 0 && tp > 0
    ? (actual / (areaSqIn / 144 / 100)) / Math.pow(tp, 0.65)
    : 0;

export function runDaltCalculation(
  rect: RectangularDuct[], round: RoundDuct[], oval: OvalDuct[], trans: TransitionDuct[],
  radial: RadialElbow[], rectElbow: RectangularElbow[], roundElbow: RoundElbow[],
  testPressure: number, leakageClass: number, pressureDrop: number, calibConst: number,
): DaltTestResults {
  const surfaceArea = calcTotalSurfaceArea(rect, round, oval, trans, radial, rectElbow, roundElbow);
  const leakageFactor = calcLeakageFactor(leakageClass, testPressure);
  const allowableLeakage = calcAllowableLeakage(surfaceArea, leakageFactor);
  const actualLeakage = calcActualLeakage(pressureDrop, calibConst);
  const testLeakageClass = calcTestLeakageClass(actualLeakage, surfaceArea, testPressure);
  const percentOfAllowable = allowableLeakage > 0 ? (actualLeakage / allowableLeakage) * 100 : 0;
  return {
    surfaceArea, actualLeakage, allowableLeakage, leakageFactor,
    testLeakageClass, percentOfAllowable, passed: percentOfAllowable <= 100,
  };
}
